import { translate } from '@teamleader/i18n';
import cx from 'classnames';
import React, { useCallback, useContext, useRef, useState } from 'react';

import styles from './styles.css';
import useCurrencyFormatter from '../../../../../Components/Context/Hooks/useCurrencyFormatter';
import Box from '../../../../../Components/UI/Box';
import Link, { SIZES } from '../../../../../Components/UI/Link';
import Popover from '../../../../../Components/UI/Popover';
import { InvoiceContext } from '../../../Context';

const PaymentBreakdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const { invoice } = useContext(InvoiceContext);
  const formatter = useCurrencyFormatter(invoice?.total.due.currency);

  const handleOpenDropdown = useCallback(() => setIsOpen(true), []);
  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);

  if (!invoice) {
    return null;
  }

  return (
    <>
      <Link size={SIZES.SMALL} ref={ref} onClick={handleOpenDropdown}>
        {translate('actions.viewCalculation')}
      </Link>
      {ref.current && isOpen && (
        <Popover
          placement="bottom"
          anchor={ref.current}
          onEscKeyDown={handleCloseDropdown}
          onOverlayClick={handleCloseDropdown}
        >
          <Box className={styles['payment-breakdown']}>
            <h5 className={styles['title']}>{translate('calculation')}</h5>
            <Box marginTop={3}>
              <Box className={cx(styles['label-value'], styles['label-value--bold'])}>
                <Box className={styles['label']}>{translate('totalInvoiceAmount')}</Box>
                <Box>{formatter.format(invoice.total.payable.amount)}</Box>
              </Box>
              {invoice.total.interest.amount > 0 && (
                <Box className={styles['label-value']}>
                  <Box className={styles['label']}>{translate('interest')}</Box>
                  <Box>+ {formatter.format(invoice.total.interest.amount)}</Box>
                </Box>
              )}
              {invoice.total.fixedLateFee.amount > 0 && (
                <Box className={styles['label-value']}>
                  <Box className={styles['label']}>{translate('fixedLateFee')}</Box>
                  <Box>+ {formatter.format(invoice.total.fixedLateFee.amount)}</Box>
                </Box>
              )}
              {invoice.total.paidAmount.amount > 0 && (
                <Box className={styles['label-value']}>
                  <Box className={styles['label']}>{translate('paid')}</Box>
                  <Box>- {formatter.format(invoice.total.paidAmount.amount)}</Box>
                </Box>
              )}
              {invoice.total.creditedAmount.amount > 0 && (
                <Box className={styles['label-value']}>
                  <Box className={styles['label']}>{translate('credited')}</Box>
                  <Box>- {formatter.format(invoice.total.creditedAmount.amount)}</Box>
                </Box>
              )}
              <Box className={cx(styles['label-value'], styles['label-value--bold'])} marginTop={3}>
                <Box className={styles['label']}>{translate('amountDue')}</Box>
                <Box>{formatter.format(invoice.total.due.amount)}</Box>
              </Box>
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default PaymentBreakdown;
